import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Card, CardContent, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { listPublicPrograms } from '../../services/publicProgramService';

// Phase C.2 — the public Program directory (`/programs`). Fully public:
// no RequireAuth wrapper anywhere in this file, and listPublicPrograms()
// calls a migration-038 RPC granted to `anon` — a logged-out visitor
// must be able to load this page exactly like a signed-in one. This is
// also the new destination for the FFN landing page's "Find Your
// Program" CTA (previously /my-programs, which requires signing in —
// see FFNLandingPage.js's own updated comment on that CTA).
function seasonLabel(program) {
  if (!program.current_season_year) return null;
  const statusWord = program.current_season_status === 'active' ? 'Season'
    : program.current_season_status === 'upcoming' ? 'Season (Upcoming)'
    : 'Season (Archived)';
  return `${program.current_season_year} ${statusWord}`;
}

function ProgramCard({ program, navigate }) {
  const location = [program.city, program.state_code].filter(Boolean).join(', ');
  const label = seasonLabel(program);
  return (
    <Card
      onClick={() => navigate(`/programs/${program.slug}`)}
      sx={{
        cursor: 'pointer', backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a',
        transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(46,168,75,0.4)' },
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{program.name}</Typography>
        {location && <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mt: 0.25 }}>{location}</Typography>}
        {label && <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mt: 0.5 }}>{label}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function PublicProgramDirectoryPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    listPublicPrograms()
      .then(setPrograms)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>Find Your Program</Typography>
        <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
          Browse Football for Nerds programs open to the public. No account needed.
        </Typography>

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : programs.length === 0 ? (
          <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <Typography sx={{ color: '#9eb8d0' }}>No public programs are available yet.</Typography>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {programs.map(p => <ProgramCard key={p.id} program={p} navigate={navigate} />)}
          </Box>
        )}
      </Container>
      <Footer />
    </Box>
  );
}
